import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  handlePreauthorizationDiagnosis,
  handleFillStepData,
  addValidationError,
  clearValidationErrors,
} from "../config/claimSlice";
import {
  handleAddLabTest,
  removeValidationError,
  setLabTests,
} from "features/preauthorization/config/preauthorizationSlice";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import DiagnosisCodeSelect from "features/preauthorization/components/DiagnosisCode Select";
import DiagnosisMorphologySelect from "features/preauthorization/components/DiagnosisMorphologySelect";
import LabTestCodeDescription from "features/preauthorization/components/LabTestCodeDescription";
import moment from "moment";
import { Gear, Plus, Trash } from "react-bootstrap-icons";
import DataTable from "react-data-table-component";
import IconButton from "components/common/IconButton";
import { ageCalculator, getToday, ShowError } from "helpers/utils";
import MultiSelect from "components/common/MultiSelect";
import WizardModal from "components/wizard/WizardModal";

const EditDiagnosisModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { conditionOnset, diagnosisiList, labTestList, accidentType } =
    useSelector((state) => state.preauthorization);
  const { preauthorizationDiagnosis, stepFormData, validationErrors } =
    useSelector((state) => state.claim);
  const birthDate = stepFormData?.birth_date;

  const { age } = ageCalculator(birthDate);
  const [isAdd, setIsAdd] = useState(false);

  const [showTypeError, setShowTypeError] = useState(false);

  const isInstitual =
    stepFormData?.claim_type?.claim_types_code?.toLowerCase() ===
    "institutional";
  const isAccident = preauthorizationDiagnosis?.some(
    (item) => item?.code === "Y96",
  );
  const { control, watch, setValue } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    setValue("accident", stepFormData?.accident);
    setValue("accident_date", stepFormData?.accident_date);
    setValue("accident_type", stepFormData?.accident_type);
    setValue("address", stepFormData?.address);
  }, [show]);

  const hanldeUpdateType = (value, id, type) => {
    // Check for principal type and Y96 code
    if (type === "type" && value === "principal") {
      const diagnosis = preauthorizationDiagnosis.find((d) => d.id === id);
      if (diagnosis?.code === "Y96") {
        setShowTypeError(true);
        return; // Prevent dispatch if invalid
      }
    }

    dispatch(
      handlePreauthorizationDiagnosis({
        type: "update",
        payload: { id, field: type, value },
      }),
    );
  };

  // useEffect(() => {
  //     setLocalLabTests(labTestList);
  // }, [labTestList]);

  const addDiagnosis = () => {
    const diagnosisCode = watch("diagnosis_code_discription");
    const diagnosisType = watch("diagnosis_type");
    const admission = watch("onAdmission");
    const condition = watch("condition_onset");
    const morphology = watch("morphology");

    // New validation check
    if (diagnosisType === "principal" && diagnosisCode?.icd_code === "Y96") {
      setShowTypeError(true);
      return;
    }

    // Rest of existing validation
    if (!watch("diagnosis_date") || !diagnosisType || !diagnosisCode) {
      setIsAdd(true);
      return;
    }
    if (isInstitual && !admission) {
      setIsAdd(true);
      return;
    }

    if (!condition) {
      setIsAdd(true);
      return;
    }
    if (
      diagnosisCode?.Morph_Code === 1 &&
      ["principal", "secondary"].includes(diagnosisType) &&
      !morphology
    ) {
      // Morphology validation
      dispatch(
        addValidationError({
          field: "morphology",
          message: "Morphology is required for this diagnosis code",
        }),
      );
      return;
    }

    const rawConditionOnset =
      watch("condition_onset")?.condonset_code ||
      watch("condition_onset") ||
      "";
    const conditionOnsetCode =
      conditionOnset?.find(
        (c) =>
          String(c.condonset_code).toLowerCase() ===
            String(rawConditionOnset).toLowerCase() ||
          String(c.condonset_name).toLowerCase() ===
            String(rawConditionOnset).toLowerCase(),
      )?.condonset_code || rawConditionOnset;

    dispatch(
      handlePreauthorizationDiagnosis({
        type: "add",
        payload: {
          id: Date.now(),
          date: moment(watch("diagnosis_date")).format("DD-MM-YYYY"),
          type: diagnosisType,
          code: diagnosisCode?.icd_code,
          description: diagnosisCode?.icd_description,
          level: diagnosisCode?.level,
          effective_from: diagnosisCode?.effective_from,
          inactive: diagnosisCode?.inactive,
          reactivated: diagnosisCode?.reactivated,
          sex: diagnosisCode?.sex,
          Stype: diagnosisCode?.Stype,
          AgeL: diagnosisCode?.AgeL,
          AgeH: diagnosisCode?.AgeH,
          Atype: diagnosisCode?.Atype,
          RDiag: diagnosisCode?.RDiag,
          Morph_Code: diagnosisCode?.Morph_Code,
          RTA_diag: diagnosisCode?.RTA_diag,
          concept_change: diagnosisCode?.concept_change,
          UnacceptPDx: diagnosisCode?.UnacceptPDx,
          condition_onset: conditionOnsetCode,
          onAdmission: watch("onAdmission") || "",
        },
      }),
    );

    // Reset form fields
    setValue("diagnosis_date", getToday());
    setValue("diagnosis_type", {});
    setValue("diagnosis_code_discription", null);
    setValue("condition_onset", "");
    setValue("morphology", null); // Reset morphology
    setValue("onAdmission", null);
    setIsAdd(false);
  };

  const handleSave = () => {
    // dispatch(clearValidationErrors());
    let shouldBlock = false;
    const hasDiagnosis = preauthorizationDiagnosis?.length > 0;
    const hasPrincipalDiagnosis = preauthorizationDiagnosis?.some(
      (diagnosis) => diagnosis.type === "principal",
    );
    const principalsDiagnosis = preauthorizationDiagnosis?.filter(
      (item) => item?.type === "principal",
    );
    const PDXPrinciple = preauthorizationDiagnosis?.some(
      (item) => item.UnacceptPDx === "1" && item?.type === "principal",
    );
    const hasMorphology = watch("diagnosis_morphology");

    const accidentType = watch("accident_type.accdnttype_code");
    const hasY96 = preauthorizationDiagnosis?.some((d) => d.code === "Y96");
    const isMale = preauthorizationDiagnosis?.some((d) => d.sex === "1");
    const isFemale = preauthorizationDiagnosis?.some((d) => d.sex === "2");
    const hasRTADiag = preauthorizationDiagnosis?.some(
      (d) => d?.RTA_diag === "1",
    );

    if (stepFormData?.new_born) {
      const validBirthCodes = [
        "Z38.0",
        "Z38.1",
        "Z38.2",
        "Z38.3",
        "Z38.4",
        "Z38.5",
        "Z38.6",
        "Z38.7",
        "Z38.8",
      ];
      const hasValidBirthDiagnosis = preauthorizationDiagnosis?.some(
        (d) => d.type === "secondary" && validBirthCodes.includes(d.code),
      );
      if (!hasValidBirthDiagnosis) {
        dispatch(
          addValidationError({
            field: "diagnosis",
            message:
              "Birth Related diagnosis with type secondary is required when Newborn selected.",
          }),
        );
        shouldBlock = true;
      }
    }

    const getPatientCode = (age) => {
      const { years = 0, months = 0, days = 0 } = age || {};
      if (years > 0) {
        if (years >= 100) return 300 + (years - 100);
        return 200 + years;
      } else if (months > 0) {
        return 100 + months;
      } else if (days > 0) {
        if (days <= 6) return days;
        const weeks = Math.floor(days / 7);
        if (weeks >= 1 && weeks <= 3) return 10 + weeks;
      }
      return 0;
    };

    const patientCode = getPatientCode(age);
    const isInstitutional =
      stepFormData?.claim_type?.claim_types_name === "Institutional";

    if (!isInstitutional) {
      const invalidDiagnosisTypes = preauthorizationDiagnosis?.some(
        (d) => d.type === "admitting" || d.type === "discharge",
      );
      if (invalidDiagnosisTypes) {
        dispatch(
          addValidationError({
            field: "diagnosis",
            message:
              "Admitting/Discharge diagnosis types cannot be used in non-Institutional preauthorization",
          }),
        );
        shouldBlock = true;
      }
    }

    if (hasY96 && accidentType !== "WPA") {
      dispatch(
        addValidationError({
          field: "accident_type",
          message: "Diagnosis code Y96 requires Workplace Accident (WPA) type",
        }),
      );
      shouldBlock = true;
    }

    if (accidentType === "WPA" && !hasY96) {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message: "Workplace Accident (WPA) type must have Diagnosis code Y96",
        }),
      );
      shouldBlock = true;
    }

    if (hasRTADiag && accidentType !== "MVA") {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message:
            "One of the Diagnosis (principal/Secondary) need reporting Accident type 'Motor Vehicle Accident'",
        }),
      );
      shouldBlock = true;
    }

    if (!hasRTADiag && accidentType === "MVA") {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message:
            "Motor Vehicle Accident (MVA) type must have Diagnosis related with Road Traffic Accident",
        }),
      );
      shouldBlock = true;
    }

    if (PDXPrinciple) {
      const invalidDiagnosis = preauthorizationDiagnosis.find(
        (item) => item.UnacceptPDx === "1" && item?.type === "principal",
      );
      const diagnosisCode = invalidDiagnosis?.code || "Invalid Code";
      dispatch(
        addValidationError({
          field: "diagnosis",
          message: `Diagnosis code ${diagnosisCode} cannot be selected as Principal.`,
        }),
      );
      shouldBlock = true;
    }

    if (!hasDiagnosis) {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message: "Please add at least one diagnosis",
        }),
      );
      shouldBlock = true;
    } else if (!hasPrincipalDiagnosis) {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message: "One Diagnosis code with type Principal is required.",
        }),
      );
      shouldBlock = true;
    } else if (principalsDiagnosis?.length > 1) {
      dispatch(
        addValidationError({
          field: "diagnosis",
          message: "Only One Diagnosis with Principle is allowed",
        }),
      );
      shouldBlock = true;
    }

    if (hasMorphology) {
      const hasValidMorphCode = preauthorizationDiagnosis?.some(
        (d) =>
          (d.type === "principal" || d.type === "secondary") &&
          d?.Morph_Code === "1",
      );
      if (!hasValidMorphCode) {
        dispatch(
          addValidationError({
            field: "diagnosis",
            message:
              "Morphology related diagnosis code with type principal/secondary is required when Morphology Code given.",
          }),
        );
        shouldBlock = true;
      }
    }

    preauthorizationDiagnosis?.forEach((diagnosis, index) => {
      const ageL = parseInt(diagnosis?.AgeL, 10);
      const ageH = parseInt(diagnosis?.AgeH, 10);
      const pationAType = diagnosis?.Atype === "2";
      const patienSType = diagnosis?.Stype === "2";

      if (diagnosis?.Morph_Code == 1 && !hasMorphology) {
        dispatch(
          addValidationError({
            field: `diagnosis`,
            message: `Morphology Must be selected for diagnosis #${
              index + 1
            } (${diagnosis.code})`,
          }),
        );
        shouldBlock = true;
      }

      if (!diagnosis.condition_onset?.trim()) {
        dispatch(
          addValidationError({
            field: `diagnosis`,
            message: `Condition onset is required for diagnosis #${
              index + 1
            } (${diagnosis.code})`,
          }),
        );
        shouldBlock = true;
      }
      if (!isNaN(ageL) && patientCode < ageL) {
        if (pationAType) {
          console.warn("This Diagnosis Cannot be used for patient this age.");
        } else {
          dispatch(
            addValidationError({
              field: "diagnosis",
              message: "This Diagnosis Cannot be used for patient this age.",
            }),
          );
          shouldBlock = true;
        }
      }
      if (!isNaN(ageH) && patientCode > ageH) {
        if (pationAType) {
          console.warn("This Diagnosis is rare to use with this patient age.");
        } else {
          dispatch(
            addValidationError({
              field: "diagnosis",
              message: "This Diagnosis Cannot be used for patient this age.",
            }),
          );
          shouldBlock = true;
        }
      }
      if (watch("gender")?.genders_disply === "Male" && isFemale) {
        if (patienSType) {
          console.warn("This Diagnosis is rare to use with this patient Type.");
        } else {
          dispatch(
            addValidationError({
              field: "diagnosis",
              message: "Diagnosis can't be used for Male patient",
            }),
          );
          shouldBlock = true;
        }
      }
      if (watch("gender")?.genders_disply === "Female" && isMale) {
        if (patienSType) {
          console.warn("This Diagnosis is rare to use with this patient Type.");
        } else {
          dispatch(
            addValidationError({
              field: "diagnosis",
              message: "Diagnosis can't be used for Female patient",
            }),
          );
          shouldBlock = true;
        }
      }
    });

    if (shouldBlock) return;
    dispatch(clearValidationErrors());
    const newMorphology = watch("diagnosis_morphology");
    // setMorphologyData(newMorphology);
    dispatch(handleFillStepData({ check: "morphology", value: newMorphology }));
    dispatch(
      handleFillStepData({ check: "accident", value: watch("accident") }),
    );
    dispatch(
      handleFillStepData({
        check: "accident_date",
        value: watch("accident_date"),
      }),
    );
    dispatch(
      handleFillStepData({
        check: "accident_type",
        value: watch("accident_type"),
      }),
    );
    dispatch(handleFillStepData({ check: "address", value: watch("address") }));
    dispatch(
      handleFillStepData({ check: "street_name", value: watch("street_name") }),
    );
    dispatch(handleFillStepData({ check: "city", value: watch("city") }));
    dispatch(handleFillStepData({ check: "country", value: watch("country") }));
    onHide();
    // setIsEditing(false);
  };

  const onAdmissionOptions = [
    { value: "y", label: "Yes" },
    { value: "n", label: "No" },
    { value: "u", label: "Unknown" },
  ];

  const deleteRow = (id) => {
    dispatch(
      handlePreauthorizationDiagnosis({
        type: "delete",
        payload: { id },
      }),
    );
  };
  const handleAddLab = () => {
    if (!watch("lab_test_code")) {
      dispatch(
        addValidationError({
          field: "Lab Test Code Description",
          message: "Lab Test Code Description is required",
        }),
      );
      return;
    } else {
      dispatch(removeValidationError("Lab Test Code Description"));
    }

    if (!watch("quantity")) {
      dispatch(
        addValidationError({
          field: "Lab Test Quantity",
          message: "Lab Test Quantity is required",
        }),
      );
      return;
    } else {
      dispatch(removeValidationError("Lab Test Quantity"));
    }

    dispatch(
      handleAddLabTest({
        type: "ADDLABTEST",
        payload: {
          id: Date.now(), // Add unique ID
          lab_test_code: watch("lab_test_code"),
          quantity: watch("quantity"),
        },
      }),
    );

    setValue("lab_test_code", null);
    setValue("quantity", "");
  };

  useEffect(() => {
    setValue("accident", stepFormData?.accident);
    setValue("accident_type", stepFormData?.accident_type);
    setValue("accident_date", stepFormData?.accident_date);
    setValue("address", stepFormData?.address);
  }, []);

  const institutionalColumns =
    stepFormData?.claim_type?.claim_types_code === "institutional"
      ? [
          {
            name: "On Admission",
            sortable: false,
            omit: false,
            cell: (row) => (
              <div className="d-flex">
                <Form.Select
                  value={`${row?.onAdmission}` || ""}
                  className="fs-10"
                  onChange={(e) =>
                    hanldeUpdateType(e.target.value, row?.id, "onAdmission")
                  }
                >
                  <option value="">Select Type</option>
                  <option value="y">Yes</option>
                  <option value="n">No</option>
                  <option value="u">Unknown</option>
                </Form.Select>
              </div>
            ),
          },
        ]
      : [];

  const columns = [
    {
      name: "",
      selector: (_, index) => (
        <div
          className="rounded-circle bg-200 d-flex flex-center fs-10 mx-auto"
          style={{ width: "20px", height: "20px", borderRadius: "50%" }}
        >
          {index + 1}
        </div>
      ),
      width: "4rem",
    },
    {
      name: "Date",
      selector: (row) => (
        <div className="text-center mx-auto fs-10">{row?.date || "-"}</div>
      ),
      width: "8rem",
    },
    {
      name: "Type",
      cell: (row) => (
        <Form.Select
          value={row?.type || ""}
          className="fs-10"
          onChange={(e) => hanldeUpdateType(e.target.value, row?.id, "type")}
        >
          <option value="">Select Type</option>
          {diagnosisiList?.map((item) => (
            <option key={item?.diagtype_code} value={item?.diagtype_code}>
              {item?.diagtype_name}
            </option>
          ))}
        </Form.Select>
      ),
    },
    {
      name: "Condition Onset",
      cell: (row) => (
        <Form.Select
          value={row?.condition_onset || ""}
          className="fs-10"
          onChange={(e) =>
            hanldeUpdateType(e.target.value, row?.id, "condition_onset")
          }
        >
          <option value="">Select Type</option>
          {conditionOnset?.map((item) => (
            <option key={item?.condonset_code} value={item?.condonset_code}>
              {item?.condonset_name}
            </option>
          ))}
        </Form.Select>
      ),
    },
    ...institutionalColumns,
    {
      name: "Diagnosis Info",
      selector: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          ({row?.code}) - {row?.description}
        </div>
      ),
    },
    {
      name: <Gear />,
      button: true,
      cell: (row) => (
        <IconButton
          size="sm"
          variant="danger"
          onClick={() => deleteRow(row?.id)}
        >
          <Trash />
        </IconButton>
      ),
    },
  ];

  const labTestColumns = [
    {
      name: "#",
      sortable: false,
      omit: false,
      selector: (_, index) => (
        <div
          className="rounded-circle bg-200 d-flex flex-center fs-10 mx-auto"
          style={{ width: "20px", height: "20px", borderRadius: "50%" }}
        >
          {index + 1}
        </div>
      ),
      width: "4rem",
    },
    { name: "Lab Code", selector: (row) => row.lab_test_code?.loinc_code },
    {
      name: "Test Name",
      selector: (row) => row.lab_test_code?.loinc_description,
    },
    {
      name: "Lionc UOM",
      selector: (row) => row.lab_test_code?.loinc_uom,
    },
    { name: "Quantity", selector: (row) => row.quantity },
    {
      name: "Action",
      button: true,
      selector: (row) => (
        <IconButton
          size="sm"
          variant="danger"
          onClick={() => handleDeleteClick(row.id)}
        >
          <Trash size={15} />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Modal show={show} size="xl">
        <Modal.Header closeButton onHide={onHide}>
          <Modal.Title className="fs-11">
            Edit Diagnosis Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="align-items-center">
              <Col xs={12}>
                <Row>
                  <Col xs={8} md={2}>
                    <h5 className="fs-10">Accident?</h5>
                  </Col>
                  <Col xs={4} md={10}>
                    <Controller
                      name="accident"
                      control={control}
                      rules={{ required: false }}
                      defaultValue={false}
                      render={({ field }) => (
                        <Form.Switch
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="lg"
                        />
                      )}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            {watch("accident") && (
              <Row className="mb-3">
                <Col md={6} lg={4} xl={3}>
                  <BootDatePicker
                    name="accident_date"
                    label="Accident Date"
                    control={control}
                    rules={{ required: watch("accident") }}
                    isRequired={watch("accident")}
                    placeholder="dd/mm/yyyy"
                    selected={watch("accident_date")}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} xl={3}>
                  <label className="form-label">
                    Accident Type <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="accident_type"
                    control={control}
                    rules={{ required: watch("accident") }}
                    render={({ field }) => (
                      <MultiSelect
                        {...field}
                        isMulti={false}
                        isClearable
                        menuPlacement="auto"
                        closeMenuOnSelect={false}
                        placeholder="Choose ..."
                        value={
                          field.value
                            ? {
                                value: field.value.accdnttype_code,
                                label: field.value.accdnttype_name,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          setValue(
                            "accident_type",
                            selected
                              ? {
                                  accdnttype_code: selected.value,
                                  accdnttype_name: selected.label,
                                }
                              : null,
                          )
                        }
                        formatOptionLabel={({ label }) => <div>{label}</div>}
                        getOptionValue={(option) => option.value}
                        options={accidentType?.map((item) => ({
                          value: item.accdnttype_code,
                          label: item.accdnttype_name,
                        }))}
                        isDisabled={isAccident}
                      />
                    )}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextInput
                    name="address"
                    control={control}
                    label="Address"
                    size="sm"
                    placeholder="Address"
                  />
                </Col>
              </Row>
            )}

            <Row className="align-items-end">
              <Col xs={12} sm={6} md={4} xl={2}>
                <BootDatePicker
                  name="diagnosis_date"
                  label="Diagnosis Date"
                  control={control}
                  rules={{ required: "Diagnosis Date is required" }}
                  size="sm"
                  isRequired={true}
                  placeholder="dd/mm/yyyy"
                  selected={watch("diagnosis_date")}
                />
              </Col>
              <Col xs={12} sm={6} md={4} xl={2}>
                <Controller
                  name="diagnosis_type"
                  control={control}
                  rules={{ required: "Diagnosis Type is required" }}
                  render={({ field }) => (
                    <Form.Group>
                      <Form.Label className="form-label">
                        Diagnosis Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select {...field} size="sm">
                        <option value="">Select Type</option>
                        {diagnosisiList.map((type) => (
                          <option
                            key={type.diagtype_code}
                            value={type.diagtype_code}
                          >
                            {type.diagtype_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                />
              </Col>
              <Col xs={12} sm={6} md={4} xl={2}>
                <Controller
                  name="condition_onset"
                  control={control}
                  rules={{ required: "Condition Onset is required" }}
                  render={({ field }) => (
                    <Form.Group>
                      <Form.Label className="form-label">
                        Condition Onset <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select {...field} size="sm">
                        <option value="">Select Type</option>
                        {conditionOnset.map((type) => (
                          <option
                            key={type.condonset_code}
                            value={type.condonset_code}
                          >
                            {type.condonset_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                />
              </Col>
              {stepFormData?.claim_type?.claim_types_code?.toLowerCase() ===
                "institutional" && (
                <Col xs={12} sm={6} md={4} xl={2}>
                  <Controller
                    name="onAdmission"
                    control={control}
                    rules={{ required: "On Admission is required" }}
                    render={({ field }) => (
                      <Form.Group>
                        <Form.Label className="form-label">
                          On Admission <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select {...field} size="sm">
                          <option value="">Select</option>
                          {onAdmissionOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                  />
                </Col>
              )}
              <Col
                xs={12}
                md={6}
                xl={
                  stepFormData?.claim_type?.claim_types_code?.toLowerCase() ===
                  "institutional"
                    ? 4
                    : 6
                }
              >
                <Row className="align-items-end">
                  <Col xs={10}>
                    <DiagnosisCodeSelect
                      control={control}
                      setValue={setValue}
                      watch={watch}
                      onDiagnosisSelected={(code, description, data) => {
                        setValue("diagnosisData", {
                          code,
                          description,
                          ...data,
                        });
                      }}
                    />
                  </Col>
                  <Col xs={2}>
                    <Button variant="success" size="sm" onClick={addDiagnosis}>
                      <Plus size={20} />
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <div className="mt-3">
              <DataTable
                columns={columns}
                data={preauthorizationDiagnosis}
                noHeader
                dense
                responsive
              />
            </div>
            <div className=" rounded p-3 mt-3 mb-3">
              <Row className="align-items-center">
                <Col xs={2}>
                  <h5 className="fs-10">Morphology</h5>
                </Col>
                <Col xs={10}>
                  <DiagnosisMorphologySelect
                    control={control}
                    disabled={false}
                    setValue={setValue}
                    watch={watch}
                  />
                </Col>
              </Row>
              <Row className="align-items-center mt-3">
                <Col xs={12} sm={12} xl={6}>
                  <label className="form-label">Lab Test Code</label>
                  <LabTestCodeDescription
                    control={control}
                    setValue={setValue}
                  />
                </Col>
                <Col xs={12} sm={12} xl={6}>
                  <Row>
                    <Col xs={10}>
                      <TextInput
                        name="quantity"
                        label="Quantity"
                        size="sm"
                        required={false}
                        control={control}
                        placeholder="Quantity"
                        rules={{ required: false }}
                      />
                    </Col>
                    <Col xs={2}>
                      <IconButton
                        onClick={handleAddLab}
                        size="sm"
                        variant="success"
                        className="mt-4"
                      >
                        <Plus size={20} />
                      </IconButton>
                    </Col>
                  </Row>
                </Col>
                {labTestList?.length > 0 && (
                  <Col xs={12} className="mt-3">
                    <DataTable
                      data={labTestList}
                      columns={labTestColumns}
                      responsive
                      customStyles={{
                        table: { style: { overflow: "visible" } },
                        rows: { style: { overflow: "visible" } },
                      }}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save All
          </Button>
        </Modal.Footer>
        <ShowError errors={[]} validationErrors={validationErrors} />
        <WizardModal
          modal={showTypeError}
          setModal={() => setShowTypeError(false)}
          title="Type Error"
          description="This Type can't be Principal"
        />
      </Modal>
    </>
  );
};

export default EditDiagnosisModal;
